import React, { useState } from 'react';
import {
  BellIcon,
  Cog6ToothIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  CheckIcon,
  TrashIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';
import { useNotifications } from '../contexts/NotificationContext';
import { NotificationType, NotificationPriority } from '../types/notification';

const Notifications: React.FC = () => {
  const {
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    clearAllNotifications,
  } = useNotifications();

  const [filter, setFilter] = useState<'all' | 'unread' | NotificationType>('all');
  const [searchTerm, setSearchTerm] = useState('');

  const filteredNotifications = notifications.filter((notification) => {
    // Filter by read status or type
    if (filter === 'unread' && notification.isRead) return false;
    if (filter !== 'all' && filter !== 'unread' && notification.type !== filter) return false;

    // Filter by search term
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      return (
        (notification.title ?? '').toLowerCase().includes(searchLower) ||
        (notification.message ?? '').toLowerCase().includes(searchLower)
      );
    }

    return true;
  });

  const getIconColor = (type: NotificationType, priority: NotificationPriority) => {
    if (priority === NotificationPriority.URGENT) return 'text-red-500';
    if (priority === NotificationPriority.HIGH) return 'text-orange-500';

    switch (type) {
      case NotificationType.BOOKING:
        return 'text-green-500';
      case NotificationType.PAYMENT:
        return 'text-blue-500';
      case NotificationType.FLEET:
        return 'text-purple-500';
      case NotificationType.MAINTENANCE:
        return 'text-yellow-500';
      case NotificationType.SECURITY:
        return 'text-red-500';
      case NotificationType.SYSTEM:
        return 'text-gray-500';
      case NotificationType.BLOG:
        return 'text-indigo-500';
      case NotificationType.USER:
        return 'text-teal-500';
      default:
        return 'text-gray-500';
    }
  };

  const getNotificationIcon = (type: NotificationType, priority: NotificationPriority) => {
    const iconClass = `w-6 h-6 ${getIconColor(type, priority)}`;

    switch (type) {
      case NotificationType.BOOKING:
        return <CheckIcon className={iconClass} />;
      case NotificationType.MAINTENANCE:
        return <Cog6ToothIcon className={iconClass} />;
      case NotificationType.PAYMENT:
      case NotificationType.FLEET:
      case NotificationType.SECURITY:
      case NotificationType.SYSTEM:
      case NotificationType.BLOG:
      case NotificationType.USER:
      default:
        return <BellIcon className={iconClass} />;
    }
  };

  const getPriorityBadge = (priority: NotificationPriority) => {
    const baseClass = 'px-3 py-1 text-xs font-medium rounded-full';

    switch (priority) {
      case NotificationPriority.URGENT:
        return `${baseClass} bg-red-100 text-red-800`;
      case NotificationPriority.HIGH:
        return `${baseClass} bg-orange-100 text-orange-800`;
      case NotificationPriority.MEDIUM:
        return `${baseClass} bg-yellow-100 text-yellow-800`;
      case NotificationPriority.LOW:
      default:
        return `${baseClass} bg-gray-100 text-gray-800`;
    }
  };

  // ✅ Works with Date OR string OR number
  const formatTimeAgo = (input: Date | string | number) => {
    const date = input instanceof Date ? input : new Date(input);

    if (Number.isNaN(date.getTime())) return 'Unknown time';

    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));

    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes} minutes ago`;

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours} hours ago`;

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays} days ago`;

    return date.toLocaleDateString();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 p-6 space-y-8">
      {/* Header */}
      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
        <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-6">
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
                <BellIcon className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
                  Notifications
                </h1>
                <p className="text-gray-600 text-lg">Stay updated with your car rental business</p>
              </div>
            </div>
          </div>

          <div className="flex gap-3">
            <div className="px-6 py-3 rounded-xl font-medium bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg">
              <BellIcon className="w-5 h-5 inline mr-2" />
              Notifications
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search notifications..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Filter */}
          <div className="flex items-center gap-2">
            <FunnelIcon className="w-5 h-5 text-gray-500" />
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value as any)}
              className="px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Notifications</option>
              <option value="unread">Unread ({unreadCount})</option>
              <option value={NotificationType.BOOKING}>Bookings</option>
              <option value={NotificationType.PAYMENT}>Payments</option>
              <option value={NotificationType.FLEET}>Fleet</option>
              <option value={NotificationType.MAINTENANCE}>Maintenance</option>
              <option value={NotificationType.SECURITY}>Security</option>
              <option value={NotificationType.SYSTEM}>System</option>
              <option value={NotificationType.BLOG}>Blog</option>
              <option value={NotificationType.USER}>Users</option>
            </select>
          </div>

          {/* Actions */}
          <div className="flex gap-2">
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="px-4 py-3 bg-green-100 text-green-700 rounded-xl hover:bg-green-200 transition-colors flex items-center gap-2"
              >
                <EyeIcon className="w-4 h-4" />
                Mark All Read
              </button>
            )}

            <button
              onClick={clearAllNotifications}
              className="px-4 py-3 bg-red-100 text-red-700 rounded-xl hover:bg-red-200 transition-colors flex items-center gap-2"
            >
              <TrashIcon className="w-4 h-4" />
              Clear All
            </button>
          </div>
        </div>
      </div>

      {/* Notifications List */}
      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
        {filteredNotifications.length === 0 ? (
          <div className="p-12 text-center">
            <BellIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No notifications found</h3>
            <p className="text-gray-600">
              {searchTerm
                ? `No notifications match "${searchTerm}"`
                : filter === 'unread'
                  ? 'All caught up! No unread notifications.'
                  : 'You have no notifications at the moment.'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {filteredNotifications.map((notification) => {
              const safePriority = notification.priority ?? NotificationPriority.MEDIUM;
              const safeType = notification.type ?? NotificationType.SYSTEM;

              return (
                <div
                  key={notification.id}
                  className={`p-6 hover:bg-gray-50 transition-colors ${
                    !notification.isRead ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
                  }`}
                >
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 mt-1">
                      {getNotificationIcon(safeType, safePriority)}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <h3
                            className={`text-lg font-semibold ${
                              !notification.isRead ? 'text-gray-900' : 'text-gray-700'
                            }`}
                          >
                            {notification.title}
                          </h3>

                          <p className="text-gray-600 mt-1 leading-relaxed">{notification.message}</p>

                          <div className="flex items-center gap-4 mt-3">
                            <span className={getPriorityBadge(safePriority)}>
                              {String(safePriority).toUpperCase()}
                            </span>

                            <span className="text-sm text-gray-500">
                              {formatTimeAgo(notification.createdAt as any)}
                            </span>

                            <span className="text-sm text-gray-500 capitalize">
                              {String(safeType).replaceAll('_', ' ')}
                            </span>
                          </div>

                          {notification.actionLabel && (
                            <div className="mt-3">
                              <button className="text-blue-600 hover:text-blue-700 font-medium text-sm">
                                {notification.actionLabel} →
                              </button>
                            </div>
                          )}
                        </div>

                        <div className="flex flex-col items-end gap-2">
                          {!notification.isRead && <div className="w-3 h-3 bg-blue-500 rounded-full"></div>}

                          <div className="flex gap-1">
                            {!notification.isRead && (
                              <button
                                onClick={() => markAsRead(notification.id)}
                                className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                                title="Mark as read"
                              >
                                <CheckIcon className="w-4 h-4 text-gray-500" />
                              </button>
                            )}

                            <button
                              onClick={() => deleteNotification(notification.id)}
                              className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                              title="Delete notification"
                            >
                              <TrashIcon className="w-4 h-4 text-gray-500" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Notifications;
